import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { SPEED_RUN_EVENT, inSpeedRun, resumeSpeedRun, runSignal, speedRunLocked } from "@/lib/speedRun";
import { RUN_SIGNAL } from "@/lib/speedRunPath.core";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useUiMode } from "@/hooks/useUiMode";
import { isViewingAsSomeoneElse } from "@/lib/viewAs";

/****************
 * Switch to Pro / Switch to Lite (Guillaume 2026-10-22). `compact` is the
 * sidebar's one line; the full card is Settings'. Words: common.json mode.*.
 ****************/
export function UiModeSwitch({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const { mode, setMode, ready } = useUiMode();
  const [lockOpen, setLockOpen] = useState(false);
  // Redrawn when the run moves on: its Pro step opens the lock (11-02 audit: the 🔒 stayed).
  const [, redraw] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return undefined;
    const on = () => redraw((n) => n + 1);
    window.addEventListener(SPEED_RUN_EVENT, on);
    return () => window.removeEventListener(SPEED_RUN_EVENT, on);
  }, []);
  if (!ready || isViewingAsSomeoneElse()) return null;
  const other = mode === "lite" ? "pro" : "lite";
  // A speed run stays in Lite until its first session (lib/speedRun.ts).
  const locked = other === "pro" && speedRunLocked();
  const press = () => {
    // Locked during a speed run (Guillaume 11-01): a pop-up sends the person back
    // to the tutorial; nothing switches.
    // Read at the press, not at the last render: the run may have reached its Pro step since (11-02 audit).
    if (other === "pro" && speedRunLocked()) { setLockOpen(true); return; }
    setMode(other);
    if (other === "pro" && inSpeedRun()) runSignal(RUN_SIGNAL.proOpened);
  };
  const control = (
    <>
      <button type="button" onClick={press} data-testid="ui-mode-switch"
        aria-disabled={locked || undefined}
        title={locked ? t("mode.lockedSpeedRun") : undefined}
        // Glows so it is found (Guillaume's /preview review #5 and #30, 10-25).
        className={`w-full rounded-lg ${locked ? "opacity-60" : ""} border border-primary/60 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-foreground shadow-[0_0_14px_rgba(59,130,246,0.55)] hover:bg-primary/20 transition-colors`}>
        {locked ? `🔒 ${t("mode.switchToPro")}` : other === "pro" ? t("mode.switchToPro") : t("mode.switchToLite")}
      </button>
      <Dialog open={lockOpen} onOpenChange={setLockOpen}>
        <DialogContent className="max-w-sm" data-testid="pro-lock-dialog">
          <DialogHeader>
            <DialogTitle>{t("mode.lockedTitle")}</DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => { setLockOpen(false); resumeSpeedRun(); }}>
              {t("mode.continueTutorial")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
  // The speed run's last step glows here (lib/speedRunPath.core.ts "switch-pro").
  const switchControl = other === "pro" ? <div data-tour="switch-pro">{control}</div> : control;
  if (compact) {
    return (
      <div className="space-y-1.5">
        <p className="text-[11px] text-center text-muted-foreground">{mode === "lite" ? t("mode.lite") : t("mode.pro")}</p>
        {switchControl}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-white/10 p-4 space-y-2" data-testid="ui-mode-card">
      <p className="text-sm font-semibold">{mode === "lite" ? t("mode.lite") : t("mode.pro")}</p>
      <p className="text-xs text-muted-foreground">{mode === "lite" ? t("mode.liteBody") : t("mode.proBody")}</p>
      {switchControl}
    </div>
  );
}
